import { getBusinessTravellerSelectEnabled } from 'businessTraveller/utils/getBusinessTravellerSelectEnabled'
import { getFrequentFlyerCheckoutFields } from 'businessTraveller/utils/getEmployeeFrequentFlyerCode'
import { getHotelLoyaltyCheckoutFields } from 'businessTraveller/utils/getEmployeeHotelLoyaltyCheckoutFields'
import { GIFT_RECIPIENT_FORM_ID } from 'constants/checkout'
import config from 'constants/config'
import { AGENT_HUB_DEFAULT_PHONE_NUMBER_PREFIX } from 'constants/config/region'

const defaultGiftFormValues: App.Checkout.TravellerForm = {
  type: 'new',
  id: GIFT_RECIPIENT_FORM_ID,
  travellerOptionKey: 'new-traveller',
  data: {
    id: GIFT_RECIPIENT_FORM_ID,
    firstName: '',
    lastName: '',
    scheduledDate: '',
    personalMessage: '',
  },
}

export default function getLEBTTravellerForms({
  travellerEmployees,
  travellerGuests,
  selectedEmployeeDetails,
  account,
  currentRegionCode,
  businessAccount,
  currentTravellerForms,
  isGift,
  flightItems,
  bedbankItems,
  getOffer,
}: {
  travellerEmployees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>
  travellerGuests: number
  selectedEmployeeDetails: Array<App.BusinessTraveller.EmployeeDetails> | undefined
  account: App.AuthAccount
  currentRegionCode: string
  businessAccount: App.BusinessTraveller.CurrentBusinessAccount | undefined
  currentTravellerForms: Array<App.Checkout.TravellerForm>
  isGift: boolean,
  flightItems: Array<App.Checkout.FlightItem>,
  bedbankItems: Array<App.Checkout.BedbankHotelItem>,
  getOffer: (offerId: string) => App.Offer | App.BedbankOffer | App.CruiseOffer | App.Tours.TourV2Offer | undefined,
}): Array<App.Checkout.TravellerForm> {
  const isBusinessTravellerSelectEnabled = getBusinessTravellerSelectEnabled(businessAccount)
  const hasTravellerEmployees = isBusinessTravellerSelectEnabled && travellerEmployees && travellerEmployees?.length > 0
  const isLEBTBookingOnBehalfOfEmployee = hasTravellerEmployees && (travellerEmployees[0].customerId !== account.memberId || travellerEmployees?.length > 1)
  const isLEBTBookingOnBehalfOfOnlyGuests = isBusinessTravellerSelectEnabled && !isLEBTBookingOnBehalfOfEmployee && travellerEmployees.length === 0 && travellerGuests > 0
  const isAgentHubEnabled = config.agentHub.isEnabled

  let options: Array<App.Checkout.TravellerForm> = []

  if (isLEBTBookingOnBehalfOfEmployee && selectedEmployeeDetails && selectedEmployeeDetails.length > 0) {
    options = travellerEmployees.map<App.Checkout.TravellerForm>((employee) => {
      const employeeDetails = selectedEmployeeDetails?.find(selectedEmployee => selectedEmployee.memberId === employee.customerId)
      return {
        id: '',
        type: 'employee',
        travellerOptionKey: `employee-${employee.id}`,
        data: {
          firstName: employeeDetails?.givenName ?? employee.firstName,
          lastName: employeeDetails?.surname ?? employee.lastName,
          email: employeeDetails?.email ?? employee.email,
          title: employeeDetails?.title ?? '',
          phonePrefix: employeeDetails?.phonePrefix?.replace(/^\++/, '') ?? '',
          phone: employeeDetails?.phone ?? '',
          countryOfResidence: employeeDetails?.countryCode ?? '',
          dateOfBirth: employeeDetails?.dob ?? '',
          postcode: employeeDetails?.postcode ?? '',
          ...getFrequentFlyerCheckoutFields(employee, flightItems),
          ...(bedbankItems.length > 0 ? getHotelLoyaltyCheckoutFields(employee, bedbankItems, getOffer) : {}), // Prefill hotel loyalty program member ID if available
        },
      }
    })
  } else if (isAgentHubEnabled) {
    options = [{
      id: '',
      type: 'new',
      travellerOptionKey: 'new-traveller',
      data: {
        firstName: '',
        lastName: '',
        email: '',
        prefix: AGENT_HUB_DEFAULT_PHONE_NUMBER_PREFIX[currentRegionCode] || '',
        phone: '',
        countryOfResidence: currentRegionCode,
      },
    }]
  } else if (!isLEBTBookingOnBehalfOfOnlyGuests) {
    options = [{
      id: '',
      type: 'account-holder',
      travellerOptionKey: 'account-holder',
      data: {
        firstName: account.givenName,
        lastName: account.surname,
        email: account.email,
        phonePrefix: account.phonePrefix?.replace(/^\++/, '') ?? '',
        phone: account.phone ?? '',
        countryOfResidence: account.countryCode ?? '',
        dateOfBirth: account.dob,
        postcode: account.postcode ?? '',
        title: account.title ?? '',
        ...getFrequentFlyerCheckoutFields(businessAccount?.employee, flightItems),
        ...(bedbankItems.length > 0 ? getHotelLoyaltyCheckoutFields(businessAccount?.employee, bedbankItems, getOffer) : {}), // Prefill hotel loyalty program member ID if available
      },
    }]
  }

  // We have saved forms, use those
  if (currentTravellerForms.length > 0) {
    // LEBT: ensure book for self flow has frequent flyer numbers
    if (!isLEBTBookingOnBehalfOfEmployee) {
      return options
    }
    // LEBT: ensure number of traveller forms matches selected number of employees, create new forms for any guests
    if (isLEBTBookingOnBehalfOfEmployee && currentTravellerForms.length < travellerEmployees.length) {
      options = [
        ...currentTravellerForms,
        ...options.slice(currentTravellerForms.length),
      ]
    }
    // set user details values (prefilledData) when is gift
    else if (isGift && currentTravellerForms.length === 1 && currentTravellerForms[0].data.id === GIFT_RECIPIENT_FORM_ID) {
      options = [
        ...currentTravellerForms,
        ...options,
      ]
    }
  }

  return isGift ? [defaultGiftFormValues, ...options] : options
}
